import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { toast } from "sonner";
import {
  ArrowRight,
  CheckCircle,
  Building,
  FileCheck,
  Shield,
  Calendar,
  Lightbulb,
  Users,
  Clock,
  Target,
  FileText,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import useFormValidation from "../../hooks/useFormValidation";
import SEO from "../../components/SEO";
import StructuredData from "../../components/StructuredData";
import AnimateOnScroll from "../../components/AnimateOnScroll";
import { trackViewContent, trackApplicationStart, trackApplicationSubmit } from "../../utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FoundationsFirst = () => {
  const [searchParams] = useSearchParams();
  const [showApplication, setShowApplication] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [includeEfiling, setIncludeEfiling] = useState(false);
  const [formStep, setFormStep] = useState(1);

  const BASE_PRICE = 1250;
  const EFILING_PRICE = 1000;
  
  // Discount code state
  const [discountCode, setDiscountCode] = useState("");
  const [discountLoading, setDiscountLoading] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountError, setDiscountError] = useState("");
  
  // Referral code state
  const [referralCode, setReferralCode] = useState(searchParams.get("ref") || "");
  const [referralValidating, setReferralValidating] = useState(false);
  const [referralValid, setReferralValid] = useState(null);
  const [referralPartnerName, setReferralPartnerName] = useState("");
  
  const calculateSubtotal = () => BASE_PRICE + (includeEfiling ? EFILING_PRICE : 0);
  
  const totalPrice = (() => {
    const subtotal = calculateSubtotal();
    if (!appliedDiscount) return subtotal;
    
    if (appliedDiscount.discount_type === "percentage") {
      return Math.max(0, subtotal - (subtotal * appliedDiscount.discount_value / 100));
    } else {
      return Math.max(0, subtotal - appliedDiscount.discount_value);
    }
  })();

  const validateDiscountCode = async () => {
    if (!discountCode.trim()) return;
    
    setDiscountLoading(true);
    setDiscountError("");
    
    try {
      const params = new URLSearchParams({
        code: discountCode.trim(),
        program_type: "foundations_first",
        amount: calculateSubtotal().toString()
      });
      
      const response = await fetch(`${API}/programs/validate-discount?${params.toString()}`, {
        method: "POST"
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Invalid discount code");
      }
      
      setAppliedDiscount(data);
      toast.success(`Discount code "${data.code}" applied!`);
    } catch (error) {
      setDiscountError(error.message);
      setAppliedDiscount(null);
    } finally {
      setDiscountLoading(false);
    }
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode("");
    setDiscountError("");
  };

  // Validate referral code
  const validateReferralCode = async (code) => {
    if (!code || code.trim().length === 0) {
      setReferralValid(null);
      setReferralPartnerName("");
      return;
    }
    
    setReferralValidating(true);
    try {
      const response = await fetch(`${API}/referrals/validate-code/${code.trim()}`);
      const data = await response.json();
      
      setReferralValid(data.valid);
      setReferralPartnerName(data.valid ? data.partner_name : "");
      
      if (data.valid) {
        toast.success(`Referral code applied! Referred by ${data.partner_name}`);
      }
    } catch (error) {
      setReferralValid(false);
      setReferralPartnerName("");
    } finally {
      setReferralValidating(false);
    }
  };

  // Validate referral code from URL on mount
  useEffect(() => {
    const refCode = searchParams.get("ref");
    if (refCode) {
      validateReferralCode(refCode);
    }
  }, [searchParams]);

  const [formData, setFormData] = useState({
    // Contact & Practice Basics
    applicant_name: "",
    applicant_email: "",
    contact_phone: "",
    company_name: "",
    industry: "",
    industry_other: "",
    practice_location: "",
    // Vision & Readiness
    practice_vision: "",
    launch_stage: "",
    // Credentials & Paperwork
    credentials: [],
    business_paperwork_filed: "",
    business_paperwork_details: "",
    // Key Needs & Challenges
    goals: "",
    challenges: "",
    help_areas: [],
    // Timeline & Commitment
    timeline: "",
    can_commit_time: "",
    // Additional Info
    how_heard: "",
    want_website: "",
    anything_else: "",
    consent_required: false,
    consent_marketing: false,
    // Legacy fields for API compatibility
    company_size: "",
    budget: "",
  });

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const paymentParam = searchParams.get("payment");

    if (sessionId && paymentParam === "success") {
      pollPaymentStatus(sessionId);
    } else if (paymentParam === "cancelled") {
      toast.error("Payment was cancelled");
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      setPaymentStatus("timeout");
      return;
    }

    try {
      const response = await fetch(`${API}/programs/payment-status/${sessionId}`);
      const data = await response.json();

      if (data.payment_status === "paid") {
        setPaymentStatus("success");
        toast.success("Payment successful! Welcome to Foundations First!");
      } else if (data.status === "expired") {
        setPaymentStatus("expired");
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  // Validation configuration
  const validationConfig = {
    requiredFieldsByStep: {
      // Step 1: Contact & Practice Basics - company_name is optional (UI shows "if chosen")
      1: ["applicant_name", "applicant_email", "contact_phone", "industry", "practice_location"],
      // Step 2: Vision & Readiness
      2: ["practice_vision", "launch_stage"],
      // Step 3: Key Needs & Challenges
      3: ["goals", "challenges"],
      // Step 4: Timeline & Commitment
      4: ["timeline", "can_commit_time"],
      // Step 5: Consent (validated separately via consent_required checkbox)
    },
    fieldLabels: {
      applicant_name: "Your Name",
      applicant_email: "Email Address",
      contact_phone: "Phone Number",
      company_name: "Practice Name",
      industry: "Industry",
      practice_location: "Practice Location",
      practice_vision: "Practice Vision",
      launch_stage: "Launch Stage",
      goals: "Goals",
      challenges: "Challenges",
      timeline: "Timeline",
      can_commit_time: "Time Commitment",
    },
    emailFields: ["applicant_email"],
    customValidators: {
      industry_other: (value, formData) => {
        if (formData.industry === "other" && (!value || !value.trim())) {
          return "Please specify your industry";
        }
        return null;
      },
    },
  };

  const { 
    validationErrors, 
    validateStep, 
    validateAllSteps, 
    clearFieldError 
  } = useFormValidation(formData, validationConfig);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    clearFieldError(field);
  };

  const toggleArrayItem = (field, item) => {
    setFormData((prev) => {
      const currentArray = prev[field] || [];
      const newArray = currentArray.includes(item)
        ? currentArray.filter(i => i !== item)
        : [...currentArray, item];
      return { ...prev, [field]: newArray };
    });
  };

  const nextStep = () => {
    if (validateStep(formStep)) {
      setFormStep(prev => Math.min(prev + 1, 5));
    } else {
      toast.error("Please fill in all required fields before continuing");
    }
  };

  const prevStep = () => setFormStep(prev => Math.max(prev - 1, 1));

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateAllSteps()) {
      toast.error("Please fill in all required fields before submitting");
      return;
    }
    
    if (!formData.consent_required) {
      toast.error("Please accept the required consent to proceed");
      return;
    }
    
    setLoading(true);

    try {
      const applicationData = {
        program_type: "foundations_first",
        applicant_name: formData.applicant_name,
        applicant_email: formData.applicant_email,
        company_name: formData.company_name || "Not yet named",
        company_size: formData.company_size || "startup",
        industry: formData.industry === "other" ? formData.industry_other : formData.industry,
        challenges: `Challenges: ${formData.challenges}\n\nHelp Areas: ${formData.help_areas.join(", ")}`,
        goals: `Goals: ${formData.goals}\n\nVision: ${formData.practice_vision}`,
        timeline: formData.timeline,
        budget: formData.budget,
        contact_phone: formData.contact_phone,
      };

      const params = new URLSearchParams(applicationData);

      const response = await fetch(`${API}/programs/apply?${params.toString()}`, {
        method: "POST",
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.detail || "Application failed");

      setApplicationId(data.application_id);
      toast.success("Application submitted!");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!applicationId) return;
    setLoading(true);

    try {
      const params = new URLSearchParams();
      if (includeEfiling) params.append("addons", "efiling");
      if (appliedDiscount) params.append("discount_code", appliedDiscount.code);
      if (referralCode && referralValid) params.append("referral_code", referralCode);
      
      const queryString = params.toString();
      const url = `${API}/programs/checkout/${applicationId}${queryString ? `?${queryString}` : ""}`;
      
      const response = await fetch(url, {
        method: "POST",
        headers: { origin: window.location.origin },
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.detail || "Checkout failed");

      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.message);
      setLoading(false);
    }
  };

  const industryOptions = [
    { value: "primary-care", label: "Primary Care / Family Medicine" },
    { value: "pediatrics", label: "Pediatrics" },
    { value: "internal-medicine", label: "Internal Medicine" },
    { value: "psychiatry", label: "Psychiatry / Mental Health / Therapy" },
    { value: "counseling", label: "Counseling / Behavioral Health" },
    { value: "home-health", label: "Home Health Care" },
    { value: "community-care", label: "Community-Based Care" },
    { value: "adult-day", label: "Adult Day Care / Senior Services" },
    { value: "therapy", label: "Physical Therapy / OT / Speech Therapy" },
    { value: "chiropractic", label: "Chiropractic" },
    { value: "womens-health", label: "Women's Health / OB-GYN" },
    { value: "urgent-care", label: "Urgent Care / Walk-In Clinic" },
    { value: "telehealth", label: "Telehealth / Virtual Care" },
    { value: "health-tech", label: "Health-Tech Startup / Digital Health" },
    { value: "specialty", label: "Specialty Clinic" },
    { value: "laboratory", label: "Laboratory / Diagnostics" },
    { value: "pharmacy", label: "Pharmacy / Medication Management" },
    { value: "integrative", label: "Integrative / Holistic Medicine" },
    { value: "dental", label: "Dental / Oral Health" },
    { value: "other", label: "Other (please specify)" },
  ];

  const credentialOptions = [
    "MD (Doctor of Medicine)",
    "DO (Doctor of Osteopathic Medicine)",
    "NP (Nurse Practitioner)",
    "PA (Physician Assistant)",
    "RN (Registered Nurse)",
    "LPN/LVN (Licensed Practical/Vocational Nurse)",
    "PsyD/PhD (Psychologist)",
    "LCSW (Licensed Clinical Social Worker)",
    "LMFT (Licensed Marriage and Family Therapist)",
    "LPC/LPCC (Licensed Professional Counselor)",
    "OT (Occupational Therapist)",
    "PT (Physical Therapist)",
    "SLP (Speech-Language Pathologist)",
    "DC (Doctor of Chiropractic)",
    "DDS/DMD (Dentist)",
    "PharmD (Doctor of Pharmacy)",
    "State Medical License",
    "DEA Registration",
    "NPI Number",
    "CLIA Certification",
    "Business Entity Formation (LLC, Corporation)",
    "Business License",
    "Medicare/Medicaid Enrollment",
    "CAQH Registration",
    "Malpractice Insurance",
  ];

  const helpAreaOptions = [
    "Licensing & compliance",
    "Staffing & hiring",
    "Operations & workflow",
    "Patient experience",
    "Technology & EHR setup",
    "Marketing & patient acquisition",
    "Business planning & strategy",
    "Financial planning & budgeting",
    "Credentialing & payer enrollment",
    "Policies & procedures / SOPs",
    "Facility setup & equipment",
    "Billing & revenue cycle management",
    "Employee training & onboarding",
    "Leadership & team development",
    "Regulatory audit readiness",
  ];

  const pillars = [
    {
      icon: Lightbulb,
      title: "Clarity at Every Step",
      description: "No more guessing or second-guessing. We turn your vision into a step-by-step roadmap, so you always know what's next.",
    },
    {
      icon: Shield,
      title: "Compliance Without the Headaches",
      description: "Healthcare regulations are tough—but we make them simple. You'll get a custom checklist for your state and specialty.",
    },
    {
      icon: Users,
      title: "Personalized, Hands-On Support",
      description: "You're never alone in this process. Get direct access to a dedicated consultant who knows the healthcare startup landscape.",
    },
  ];

  const whatsIncluded = [
    "Kickoff & Goal Mapping: Deep-dive session to clarify your business vision, goals, and unique needs",
    "Licensing & Compliance Checklist: Custom, step-by-step guide to every required license, permit, and compliance item",
    "Day-to-Day Operations Blueprint: Personalized workflows, team structure, and role definitions",
    "Technology & Tools Shortlist: Recommendations for EHR, scheduling, billing, and communication platforms",
    "90-Day Launch Plan: Detailed, actionable timeline with milestones and quick wins",
    "Templates & Checklists: Downloadable resources for ongoing use",
    "1:1 Support: Weekly live sessions plus email access for questions and feedback",
    "Summary Report: All your deliverables in one organized, easy-to-follow document",
  ];

  const timeline = [
    {
      week: "Week 1",
      title: "Kickoff & Vision Mapping",
      items: [
        "Deep-dive kickoff session to clarify your goals, ideal client, and business vision",
        "Identify your unique strengths, potential challenges, and key priorities",
        "Receive a summary document outlining your vision, objectives, and first action steps",
      ],
    },
    {
      week: "Week 2",
      title: "Licensing & Compliance Roadmap",
      items: [
        "Custom checklist of every license, permit, and compliance step required",
        "Clear guidance on what to file, when, and where",
        "Access to templates and resources to make paperwork easier",
      ],
    },
    {
      week: "Week 3",
      title: "Operations Blueprint & Team Structure",
      items: [
        "Map out your essential workflows: patient intake, scheduling, billing",
        "Define team roles and responsibilities",
        "Receive a personalized operations blueprint and onboarding checklist",
      ],
    },
    {
      week: "Week 4",
      title: "Technology, Tools & 90-Day Launch Plan",
      items: [
        "Recommendations for EHR, scheduling, billing, and communication tools",
        "Detailed, week-by-week 90-day launch plan",
        "Final live review session to answer questions and set you up for launch",
      ],
    },
  ];

  const faqs = [
    {
      question: "What does the $1,250 fee cover?",
      answer: "The fee covers the entire 4-week Foundations First program: personalized consulting, all live sessions, your custom licensing/compliance checklist, operations blueprint, team structure, technology recommendations, a detailed 90-day launch plan, templates, and your final summary report.",
    },
    {
      question: "Who is this program designed for?",
      answer: "Foundations First is for clinicians, healthcare entrepreneurs, and small practice owners who want to launch or formalize their business the right way.",
    },
    {
      question: "Will you file paperwork or licenses for me?",
      answer: "The standard program provides you with a custom checklist and clear instructions. However, we offer an optional E-Filing add-on ($1,000) where our team handles all filing and paperwork for you.",
    },
    {
      question: "What if I need more help after the 4 weeks?",
      answer: "Many clients choose to continue working with us for implementation, systems setup, or staff training at exclusive rates.",
    },
    {
      question: "Is this only for medical practices?",
      answer: "No—Foundations First is ideal for a wide range of healthcare startups, including home health, adult day, therapy, clinics, and more.",
    },
    {
      question: "Do you offer payment plans?",
      answer: "Yes. Payment options are available—just email us at info@rnbconsultinggroup.com to explore payment plans.",
    },
  ];

  const renderFormStep = () => {
    switch (formStep) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="font-heading text-lg font-semibold text-rnb-navy border-b pb-2">
              Contact & Practice Basics
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Your Name *</Label>
                <Input
                  value={formData.applicant_name}
                  onChange={(e) => handleChange("applicant_name", e.target.value)}
                  placeholder="Full name"
                  required
                  data-testid="ff-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.applicant_email}
                  onChange={(e) => handleChange("applicant_email", e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid="ff-email"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone Number *</Label>
                <Input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => handleChange("contact_phone", e.target.value)}
                  placeholder="(555) 123-4567"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Practice Name (if chosen)</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => handleChange("company_name", e.target.value)}
                  placeholder="Your practice name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>What industry or type of healthcare service will your practice offer? *</Label>
              <Select
                value={formData.industry}
                onValueChange={(value) => handleChange("industry", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select your industry..." />
                </SelectTrigger>
                <SelectContent>
                  {industryOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {formData.industry === "other" && (
              <div className="space-y-2">
                <Label>Please specify your industry</Label>
                <Input
                  value={formData.industry_other}
                  onChange={(e) => handleChange("industry_other", e.target.value)}
                  placeholder="Describe your healthcare service"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>City and State where your practice will be located *</Label>
              <Input
                value={formData.practice_location}
                onChange={(e) => handleChange("practice_location", e.target.value)}
                placeholder="Atlanta, GA"
                required
              />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <h3 className="font-heading text-lg font-semibold text-rnb-navy border-b pb-2">
              Vision & Readiness
            </h3>
            <div className="space-y-2">
              <Label>Briefly describe your vision for your practice *</Label>
              <Textarea
                value={formData.practice_vision}
                onChange={(e) => handleChange("practice_vision", e.target.value)}
                placeholder="What kind of practice do you want to build? Who will you serve?"
                rows={4}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>What stage are you at in launching your practice? *</Label>
              <Select
                value={formData.launch_stage}
                onValueChange={(value) => handleChange("launch_stage", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select your stage..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="exploring">Just exploring the idea</SelectItem>
                  <SelectItem value="planning">Planning and research</SelectItem>
                  <SelectItem value="launching">Preparing to launch (within 3–6 months)</SelectItem>
                  <SelectItem value="operational">Already open, but need foundational support</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <h3 className="font-heading text-lg font-semibold text-rnb-navy border-b pb-2 mt-6">
              Credentials & Paperwork
            </h3>
            <div className="space-y-2">
              <Label>What professional credentials, licenses, or certifications do you already have? *</Label>
              <p className="text-xs text-rnb-gray mb-2">Select all that apply</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                {credentialOptions.map((cred) => (
                  <div key={cred} className="flex items-center space-x-2">
                    <Checkbox
                      id={`cred-${cred}`}
                      checked={formData.credentials.includes(cred)}
                      onCheckedChange={() => toggleArrayItem("credentials", cred)}
                    />
                    <label htmlFor={`cred-${cred}`} className="text-sm text-rnb-navy cursor-pointer">
                      {cred}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Have you filed any business formation paperwork (LLC, corporation, DBA, etc.) yet? *</Label>
              <Select
                value={formData.business_paperwork_filed}
                onValueChange={(value) => handleChange("business_paperwork_filed", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">No</SelectItem>
                  <SelectItem value="yes">Yes (please specify)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.business_paperwork_filed === "yes" && (
              <div className="space-y-2">
                <Label>What business paperwork have you filed?</Label>
                <Input
                  value={formData.business_paperwork_details}
                  onChange={(e) => handleChange("business_paperwork_details", e.target.value)}
                  placeholder="e.g., LLC filed in Georgia"
                />
              </div>
            )}
          </div>
        );
      case 3:
        return (
          <div className="space-y-4">
            <h3 className="font-heading text-lg font-semibold text-rnb-navy border-b pb-2">
              Key Needs & Challenges
            </h3>
            <div className="space-y-2">
              <Label>What are your top 2–3 goals for joining Foundations First? *</Label>
              <Textarea
                value={formData.goals}
                onChange={(e) => handleChange("goals", e.target.value)}
                placeholder="What do you want to achieve with this program?"
                rows={3}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>What are your biggest current challenges or concerns about starting your practice? *</Label>
              <Textarea
                value={formData.challenges}
                onChange={(e) => handleChange("challenges", e.target.value)}
                placeholder="What's holding you back or causing the most stress?"
                rows={3}
                required
                data-testid="ff-challenges"
              />
            </div>
            <div className="space-y-2">
              <Label>Are there specific areas you want extra help with? *</Label>
              <p className="text-xs text-rnb-gray mb-2">Check all that apply</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                {helpAreaOptions.map((area) => (
                  <div key={area} className="flex items-center space-x-2">
                    <Checkbox
                      id={`help-${area}`}
                      checked={formData.help_areas.includes(area)}
                      onCheckedChange={() => toggleArrayItem("help_areas", area)}
                    />
                    <label htmlFor={`help-${area}`} className="text-sm text-rnb-navy cursor-pointer">
                      {area}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-4">
            <h3 className="font-heading text-lg font-semibold text-rnb-navy border-b pb-2">
              Timeline & Commitment
            </h3>
            <div className="space-y-2">
              <Label>What is your ideal timeline to launch or optimize your practice? *</Label>
              <Select
                value={formData.timeline}
                onValueChange={(value) => handleChange("timeline", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediate">Immediately (ready to start now)</SelectItem>
                  <SelectItem value="1-month">Within 1 month</SelectItem>
                  <SelectItem value="1-3-months">1–3 months</SelectItem>
                  <SelectItem value="3-6-months">3–6 months</SelectItem>
                  <SelectItem value="6-12-months">6–12 months</SelectItem>
                  <SelectItem value="exploring">12+ months / Just exploring options</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Are you able to commit 1–2 hours per week to this program? *</Label>
              <Select
                value={formData.can_commit_time}
                onValueChange={(value) => handleChange("can_commit_time", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <h3 className="font-heading text-lg font-semibold text-rnb-navy border-b pb-2 mt-6">
              Additional Information
            </h3>
            <div className="space-y-2">
              <Label>How did you hear about Foundations First?</Label>
              <Select
                value={formData.how_heard}
                onValueChange={(value) => handleChange("how_heard", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="referral">Referral from a colleague or friend</SelectItem>
                  <SelectItem value="social">Social media</SelectItem>
                  <SelectItem value="google">Google search / Online search</SelectItem>
                  <SelectItem value="website">RNB Consulting Group website</SelectItem>
                  <SelectItem value="email">Email newsletter</SelectItem>
                  <SelectItem value="event">Attended a webinar or event</SelectItem>
                  <SelectItem value="ad">Saw an online ad</SelectItem>
                  <SelectItem value="association">Professional association or group</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Would you like our web design team to reach out about building a website?</Label>
              <Select
                value={formData.want_website}
                onValueChange={(value) => handleChange("want_website", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes, please contact me</SelectItem>
                  <SelectItem value="no">Not at this time</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Any other information you&apos;d like us to know?</Label>
              <Textarea
                value={formData.anything_else}
                onChange={(e) => handleChange("anything_else", e.target.value)}
                placeholder="Anything else we should know before your consultation?"
                rows={3}
              />
            </div>
          </div>
        );
      case 5:
        return (
          <div className="space-y-4">
            <h3 className="font-heading text-lg font-semibold text-rnb-navy border-b pb-2">
              Consent & Communications
            </h3>
            <div className="space-y-4 bg-slate-50 p-4 rounded-lg">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="consent-required"
                  checked={formData.consent_required}
                  onCheckedChange={(checked) => handleChange("consent_required", checked)}
                  required
                />
                <label htmlFor="consent-required" className="text-sm text-rnb-navy cursor-pointer">
                  <span className="font-medium">Required:</span> I understand Foundations First™ is a consulting program and does not provide legal, billing, or direct filing services. I agree to receive important emails and communications from RNB Consulting Group about my participation in the Foundations First™ program.
                </label>
              </div>
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="consent-marketing"
                  checked={formData.consent_marketing}
                  onCheckedChange={(checked) => handleChange("consent_marketing", checked)}
                />
                <label htmlFor="consent-marketing" className="text-sm text-rnb-gray cursor-pointer">
                  <span className="font-medium">Optional:</span> I would like to receive additional updates, resources, and occasional promotional offers from RNB Consulting Group.
                </label>
              </div>
            </div>
            <div className="bg-rnb-light p-4 rounded-lg">
              <h4 className="font-medium text-rnb-navy mb-2">What Happens Next:</h4>
              <ul className="text-sm text-rnb-gray space-y-2">
                <li>• <strong>Consultant Match:</strong> Within 3 business days of payment, you&apos;ll be matched with one of our healthcare consulting experts.</li>
                <li>• <strong>Detailed Intake:</strong> Before your consultation, you&apos;ll receive a comprehensive intake questionnaire to help us tailor your program.</li>
              </ul>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (paymentStatus === "success") {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-gradient-to-br from-rnb-light to-white">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 bg-rnb-teal/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-rnb-teal" />
          </div>
          <h2 className="font-heading text-3xl font-bold text-rnb-navy mb-4">
            Welcome to Foundations First!
          </h2>
          <p className="text-rnb-gray mb-6">
            Your enrollment is complete. Our team will contact you within 24 hours to schedule 
            your kickoff session.
          </p>
          <Link to="/portal">
            <Button className="btn-hover-lift">
              Go to Client Portal
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SEO 
        title="Foundations First — New Practice Setup & Compliance Help"
        description="Starting a new clinic or healthcare practice? Foundations First gives you the operational foundation you need — compliance setup, credentialing, policies, workflows, and more. From $1,250."
        ogImage="https://customer-assets.emergentagent.com/job_7865eaef-d264-4504-a997-7bd20f6ab0e8/artifacts/kaxkvcsl_How-to-Use-EHR-Software-as-a-Small-Medical-Practice-1.jpg"
        keywords="how to start a medical practice, new clinic setup help, healthcare startup consulting, practice compliance setup, credentialing help for new practice, medical practice policies and procedures"
      />
      <StructuredData data={[{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.rnbconsultinggroup.com" },
          { "@type": "ListItem", "position": 2, "name": "Programs", "item": "https://www.rnbconsultinggroup.com/services" },
          { "@type": "ListItem", "position": 3, "name": "Foundations First", "item": "https://www.rnbconsultinggroup.com/programs/foundations-first" }
        ]
      }]} />
      
      {/* Hero Section */}
      <section className="relative py-28 bg-gradient-to-br from-rnb-light to-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-rnb-teal/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimateOnScroll direction="left">
              <div>
                <div className="inline-flex items-center px-4 py-2 bg-rnb-teal/10 rounded-full mb-6">
                  <Building className="w-4 h-4 text-rnb-teal mr-2" />
                  <span className="text-sm font-medium text-rnb-navy">Healthcare Startup Consulting</span>
                </div>

                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-rnb-navy mb-6">
                  Foundations First
                </h1>

                <p className="text-xl text-rnb-navy font-medium mb-4">
                  Start Your Healthcare Business Right—4 Weeks to a Fully Mapped, Compliant, and Actionable Launch Plan
                </p>

                <p className="text-lg text-rnb-gray mb-8">
                  Personalized 1:1 consulting for clinicians and healthcare founders. From business 
                  idea to operational blueprint—get everything you need to launch with clarity and confidence.
                </p>

                <div className="flex items-end gap-4 mb-8">
                  <div>
                    <p className="text-sm text-rnb-gray mb-1">One-time investment</p>
                    <p className="font-mono-numbers text-4xl font-bold text-rnb-navy">$1,250</p>
                  </div>
                  <p className="text-sm text-rnb-gray pb-1">No hidden costs</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    size="lg"
                    className="btn-hover-lift bg-rnb-teal hover:bg-rnb-teal/90"
                    onClick={() => setShowApplication(true)}
                    data-testid="foundations-apply-btn"
                  >
                    Enroll Now – $1,250
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <Link to="/contact">
                    <Button variant="outline" size="lg">
                      <Calendar className="mr-2 h-4 w-4" />
                      Schedule Free Consultation
                    </Button>
                  </Link>
                </div>
              </div>
            </AnimateOnScroll>

            <AnimateOnScroll direction="right">
              <div className="relative hidden lg:block">
                <img
                  src="https://customer-assets.emergentagent.com/job_7865eaef-d264-4504-a997-7bd20f6ab0e8/artifacts/kaxkvcsl_How-to-Use-EHR-Software-as-a-Small-Medical-Practice-1.jpg"
                  alt="Healthcare provider consulting with a family in a clinic setting"
                  className="img-warm w-full"
                />
              </div>
            </AnimateOnScroll>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-bold text-rnb-navy mb-8 text-center">
            Launch Your Healthcare Practice with Confidence
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "End-to-end support: We don't just advise—we build your launch roadmap with you",
              "Regulatory peace of mind: Know exactly which licenses, paperwork, and compliance steps you need",
              "Operational clarity: Get your core workflows, team roles, and daily processes mapped for you",
              "Save time and money: Avoid costly missteps and wasted hours on Google",
              "Designed for first-time founders and small practice startups—no jargon, no overwhelm",
            ].map((item, index) => (
              <div key={item.title || item.name || item.label || index} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0 mt-0.5" />
                <span className="text-rnb-navy">{item}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button
              size="lg"
              className="btn-hover-lift bg-rnb-teal hover:bg-rnb-teal/90"
              onClick={() => setShowApplication(true)}
            >
              Get Started Today - Only $1,250
            </Button>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              The Foundations First Pillars
            </h2>
            <p className="text-rnb-gray">What Sets Your Launch Apart</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {pillars.map((pillar, index) => (
              <Card key={pillar.title || pillar.name || `pillar-${index}`} className="card-hover border-0 shadow-sm">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-rnb-teal/10 rounded-2xl flex items-center justify-center mb-6">
                    <pillar.icon className="w-8 h-8 text-rnb-teal" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-rnb-navy mb-4">
                    {pillar.title}
                  </h3>
                  <p className="text-rnb-gray text-sm leading-relaxed">{pillar.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-24 bg-rnb-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-bold text-white mb-8 text-center">
            Here&apos;s Exactly What&apos;s Included in Your 4-Week Program
          </h2>
          <div className="space-y-4">
            {whatsIncluded.map((item, index) => (
              <div key={item.title || item.name || item.label || index} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0 mt-0.5" />
                <span className="text-slate-200">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl font-bold text-rnb-navy mb-4">
            Transparent, One-Time Pricing—No Surprises
          </h2>
          <Card className="border-2 border-rnb-teal">
            <CardContent className="p-8">
              <p className="text-lg text-rnb-navy mb-2">Foundations First 4-Week Program</p>
              <p className="font-mono-numbers text-5xl font-bold text-rnb-navy mb-4">$1,250</p>
              <ul className="space-y-2 text-rnb-gray text-sm mb-6">
                <li>✓ No long-term contracts, no hidden costs</li>
                <li>✓ 100% satisfaction guarantee</li>
                <li>✓ Optional E-Filing add-on available (+$1,000)</li>
              </ul>
              <Button
                size="lg"
                className="btn-hover-lift bg-rnb-teal hover:bg-rnb-teal/90 w-full"
                onClick={() => setShowApplication(true)}
              >
                Enroll Now – $1,250
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-bold text-rnb-navy mb-12 text-center">
            Your 4-Week Launch Journey
          </h2>
          <div className="space-y-8">
            {timeline.map((week, index) => (
              <Card key={week.title || week.name || `week-${index}`} className="border-0 shadow-sm overflow-hidden">
                <div className="flex">
                  <div className="w-32 bg-rnb-teal text-white p-6 flex flex-col justify-center items-center">
                    <Clock className="w-6 h-6 mb-2" />
                    <span className="font-bold">{week.week}</span>
                  </div>
                  <CardContent className="p-6 flex-1">
                    <h3 className="font-heading text-lg font-semibold text-rnb-navy mb-4">
                      {week.title}
                    </h3>
                    <ul className="space-y-2">
                      {week.items.map((item, i) => (
                        <li key={item.title || item.name || item.label || i} className="flex items-start gap-2 text-sm text-rnb-gray">
                          <CheckCircle className="w-4 h-4 text-rnb-teal flex-shrink-0 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-bold text-rnb-navy mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={faq.question || faq.title || i} value={`faq-${index}`} className="border rounded-lg px-4">
                <AccordionTrigger className="text-left font-medium text-rnb-navy hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-rnb-gray">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-rnb-light">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl font-bold text-rnb-navy mb-4">
            Secure Your Spot—Start Building Your Practice Today
          </h2>
          <p className="text-rnb-gray mb-8">
            Enrollment is limited to ensure high-touch support. Click below to reserve your spot 
            and get immediate access to your kickoff session booking.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="btn-hover-lift bg-rnb-teal hover:bg-rnb-teal/90"
              onClick={() => setShowApplication(true)}
            >
              Enroll in Foundations First
            </Button>
            <Link to="/contact">
              <Button variant="outline" size="lg">
                Schedule Free Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Application Modal */}
      {showApplication && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardContent className="p-8">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="font-heading text-2xl font-bold text-rnb-navy">
                    {applicationId ? "Complete Your Enrollment" : "Foundations First™ Application"}
                  </h2>
                  <p className="text-rnb-gray text-sm mt-1">
                    {applicationId
                      ? "Choose your options and complete payment below."
                      : `Step ${formStep} of 5`}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowApplication(false);
                    setApplicationId(null);
                    setFormStep(1);
                  }}
                  className="text-rnb-gray hover:text-rnb-navy text-xl"
                >
                  ✕
                </button>
              </div>

              {applicationId ? (
                <div className="py-6">
                  <div className="text-center mb-6">
                    <CheckCircle className="w-16 h-16 text-rnb-teal mx-auto mb-4" />
                    <h3 className="font-heading text-xl font-semibold text-rnb-navy mb-2">
                      Application Submitted!
                    </h3>
                    <p className="text-rnb-gray">
                      Choose your options and complete payment below.
                    </p>
                  </div>

                  {/* E-Filing Add-on Option */}
                  <div className="border rounded-lg p-4 mb-4 bg-slate-50">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="efiling-addon"
                        checked={includeEfiling}
                        onCheckedChange={setIncludeEfiling}
                        data-testid="efiling-checkbox"
                      />
                      <div className="flex-1">
                        <label htmlFor="efiling-addon" className="flex items-center gap-2 cursor-pointer">
                          <FileText className="w-5 h-5 text-rnb-teal" />
                          <span className="font-medium text-rnb-navy">E-Filing Paperwork Service</span>
                          <span className="ml-auto font-mono-numbers font-bold text-rnb-blue">+$1,000</span>
                        </label>
                        <p className="text-sm text-rnb-gray mt-1 ml-7">
                          Prefer a hands-off experience? Our team will handle all filing and paperwork for you.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Discount Code Section */}
                  <div className="border rounded-lg p-4 mb-4 bg-slate-50">
                    <p className="text-sm font-medium text-rnb-navy mb-3">Have a discount code?</p>
                    {appliedDiscount ? (
                      <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                        <div>
                          <span className="text-green-700 font-medium">{appliedDiscount.code}</span>
                          <span className="text-green-600 text-sm ml-2">
                            ({appliedDiscount.discount_type === "percentage" 
                              ? `${appliedDiscount.discount_value}% off` 
                              : `$${appliedDiscount.discount_value} off`})
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={removeDiscount}
                          className="text-green-700 hover:text-green-900 text-sm underline"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter code"
                          value={discountCode}
                          onChange={(e) => {
                            setDiscountCode(e.target.value.toUpperCase());
                            setDiscountError("");
                          }}
                          className="flex-1"
                          data-testid="ff-discount-code-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={validateDiscountCode}
                          disabled={discountLoading || !discountCode.trim()}
                          data-testid="ff-apply-discount-btn"
                        >
                          {discountLoading ? "..." : "Apply"}
                        </Button>
                      </div>
                    )}
                    {discountError && (
                      <p className="text-red-500 text-sm mt-2">{discountError}</p>
                    )}
                  </div>

                  {/* Referral Code Section */}
                  <div className="mb-4">
                    <Label className="text-sm text-rnb-gray mb-2 block">Have a referral code? (Optional)</Label>
                    {referralValid ? (
                      <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-purple-600" />
                          <span className="text-purple-700 font-medium">
                            Referred by {referralPartnerName}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setReferralCode("");
                            setReferralValid(null);
                            setReferralPartnerName("");
                          }}
                          className="text-purple-700 hover:text-purple-900 text-sm underline"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter referral code"
                          value={referralCode}
                          onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                          className="flex-1"
                          data-testid="ff-referral-code-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => validateReferralCode(referralCode)}
                          disabled={referralValidating || !referralCode.trim()}
                          data-testid="ff-apply-referral-btn"
                        >
                          {referralValidating ? "..." : "Apply"}
                        </Button>
                      </div>
                    )}
                    {referralValid === false && referralCode && (
                      <p className="text-red-500 text-sm mt-2">Invalid referral code</p>
                    )}
                  </div>

                  {/* Price Summary */}
                  <div className="border rounded-lg p-4 mb-6 bg-white">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-rnb-gray">Foundations First Program</span>
                        <span className="font-mono-numbers text-rnb-navy">$1,250</span>
                      </div>
                      {includeEfiling && (
                        <div className="flex justify-between text-rnb-teal">
                          <span>E-Filing Paperwork Service</span>
                          <span className="font-mono-numbers">$1,000</span>
                        </div>
                      )}
                      {appliedDiscount && (
                        <>
                          <div className="flex justify-between text-rnb-gray">
                            <span>Subtotal</span>
                            <span className="font-mono-numbers">${calculateSubtotal().toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-green-600">
                            <span>Discount ({appliedDiscount.code})</span>
                            <span className="font-mono-numbers">
                              -${(calculateSubtotal() - totalPrice).toLocaleString()}
                            </span>
                          </div>
                        </>
                      )}
                      <div className="border-t pt-2 flex justify-between font-bold">
                        <span className="text-rnb-navy">Total</span>
                        <span className="font-mono-numbers text-xl text-rnb-navy">${totalPrice.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  <Button
                    size="lg"
                    className="w-full btn-hover-lift bg-rnb-teal hover:bg-rnb-teal/90"
                    onClick={handlePayment}
                    disabled={loading}
                    data-testid="foundations-pay-btn"
                  >
                    {loading ? "Processing..." : `Pay $${totalPrice.toLocaleString()} to Enroll`}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Progress indicator */}
                  <div className="flex items-center justify-between mb-6">
                    {[1, 2, 3, 4, 5].map((step) => (
                      <div key={step} className="flex items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          formStep >= step ? 'bg-rnb-teal text-white' : 'bg-slate-200 text-slate-500'
                        }`}>
                          {step}
                        </div>
                        {step < 5 && (
                          <div className={`w-8 sm:w-12 h-1 mx-1 ${
                            formStep > step ? 'bg-rnb-teal' : 'bg-slate-200'
                          }`} />
                        )}
                      </div>
                    ))}
                  </div>

                  {renderFormStep()}

                  <div className="flex justify-between pt-4">
                    {formStep > 1 ? (
                      <Button type="button" variant="outline" onClick={prevStep}>
                        <ChevronLeft className="mr-2 h-4 w-4" />
                        Previous
                      </Button>
                    ) : (
                      <div />
                    )}
                    
                    {formStep < 5 ? (
                      <Button type="button" onClick={nextStep}>
                        Next
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    ) : (
                      <Button 
                        type="submit" 
                        className="btn-hover-lift bg-rnb-teal hover:bg-rnb-teal/90" 
                        disabled={loading || !formData.consent_required}
                      >
                        {loading ? "Submitting..." : "Submit Application"}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default FoundationsFirst;
